import {proxy, ref} from "valtio";

import {IQueryParamProvider} from "@/shared/contracts";
import {EventEmitter} from "@/shared/event-emitter";
import {Stepper, type StepperInstance} from "@/shared/models/form/steppers";

export interface GuestsStepperConfig {
    initialAdults?: number;
    initialChildren?: number;
    initialPets?: number;
    minAdults?: number;
    maxAdults?: number;
    minChildren?: number;
    maxChildren?: number;
    maxPets?: number; // Set to 0 to disable pets
    maxTotalGuests?: number;
    allowsPets?: boolean; // Explicit flag for pets allowed
}

export interface GuestValidationError {
    key: string;
    params?: Record<string, string | number>;
}

export interface GuestValidationResult {
    isValid: boolean;
    errors: Array<GuestValidationError>;
}

interface GuestsStepperEvents {
    change: (value: {adults: number; children: number; pets: number}) => void;
    apply: () => void;
    open: () => void;
}

class GuestsStepperClass implements IQueryParamProvider {
    // Reactive state
    adults: StepperInstance;
    children: StepperInstance;
    pets: StepperInstance;

    // Non-reactive config
    private readonly maxAdults: number;
    private readonly maxChildren: number;
    private readonly maxTotalGuests: number;
    private readonly maxPets: number;
    private readonly allowsPets: boolean;
    public readonly events: EventEmitter<GuestsStepperEvents>;

    constructor(config: GuestsStepperConfig = {}) {
        const minAdults = config.minAdults ?? 1;
        const maxAdults = config.maxAdults ?? 99;
        const minChildren = config.minChildren ?? 0;
        const maxChildren = config.maxChildren ?? 99;
        const maxPets = config.maxPets ?? 5;

        this.maxAdults = maxAdults;
        this.maxChildren = maxChildren;
        this.maxTotalGuests = config.maxTotalGuests ?? 190;
        this.maxPets = maxPets;
        this.allowsPets = config.allowsPets ?? maxPets > 0;

        // Create steppers with actual limits
        this.adults = Stepper({
            initialValue: config.initialAdults ?? 1,
            min: minAdults,
            max: maxAdults,
            step: 1,
        });

        this.children = Stepper({
            initialValue: config.initialChildren ?? 0,
            min: minChildren,
            max: maxChildren,
            step: 1,
        });

        this.pets = Stepper({
            initialValue: config.initialPets ?? 0,
            min: 0,
            max: this.allowsPets ? maxPets : 0,
            step: 1,
            // Don't disable - allow decrement to clear invalid URL values
        });

        this.events = ref(new EventEmitter<GuestsStepperEvents>()) as EventEmitter<GuestsStepperEvents>;

        // Setup event listeners for interdependencies
        this.setupEventListeners();

        // Update dynamic limits based on maxTotalGuests
        this.updateDynamicLimits();
    }

    private setupEventListeners(): void {
        // Special handling for adults: if over limit, decrement resets to max allowed
        this.adults.events.on("decrement", () => {
            const maxAllowed = Math.min(this.maxAdults, this.maxTotalGuests - this.children.value);
            if (this.adults.value > maxAllowed) {
                this.adults.setValue(Math.max(1, maxAllowed), true);
            }
        });

        // Special handling for children: if over limit, decrement resets to max allowed
        this.children.events.on("decrement", () => {
            const maxAllowed = Math.min(this.maxChildren, this.maxTotalGuests - this.adults.value);
            if (this.children.value > maxAllowed) {
                this.children.setValue(Math.max(0, maxAllowed), true);
            }
        });

        // Special handling for pets: if not allowed, any decrement resets to 0
        this.pets.events.on("decrement", () => {
            if (!this.allowsPets && this.pets.value > 0) {
                this.pets.setValue(0, true);
            } else if (this.pets.value > this.maxPets) {
                this.pets.setValue(this.maxPets, true);
            }
        });

        // Update limits and emit change on any stepper change
        this.adults.events.on("change", () => {
            this.updateDynamicLimits();
            this.emitChange();
        });
        this.children.events.on("change", () => {
            this.updateDynamicLimits();
            this.emitChange();
        });
        this.pets.events.on("change", () => this.emitChange());
    }

    /**
     * Update stepper max values based on maxTotalGuests constraint
     * This ensures +/- buttons are disabled when at limit
     */
    private updateDynamicLimits(): void {
        const currentAdults = this.adults.value;
        const currentChildren = this.children.value;
        const remainingForAdults = this.maxTotalGuests - currentChildren;
        const remainingForChildren = this.maxTotalGuests - currentAdults;

        // Set max for adults: minimum of maxAdults and remaining space
        this.adults.setMax(Math.min(this.maxAdults, remainingForAdults));

        // Set max for children: minimum of maxChildren and remaining space
        this.children.setMax(Math.min(this.maxChildren, remainingForChildren));
    }

    /**
     * Validate current values against limits
     * Returns validation result with error keys (does NOT modify values)
     * Error keys should be translated in the view layer using i18n
     */
    validate(): GuestValidationResult {
        const errors: Array<GuestValidationError> = [];
        const currentAdults = this.adults.value;
        const currentChildren = this.children.value;
        const currentPets = this.pets.value;
        const currentTotal = currentAdults + currentChildren;

        // Check pets first (most specific error)
        if (currentPets > 0 && !this.allowsPets) {
            errors.push({key: "petsNotAllowed"});
        } else if (currentPets > this.maxPets) {
            errors.push({key: "maxPetsExceeded", params: {count: this.maxPets}});
        }

        // Check individual limits
        if (currentAdults > this.maxAdults) {
            errors.push({key: "maxAdultsExceeded", params: {count: this.maxAdults}});
        }

        if (currentChildren > this.maxChildren) {
            errors.push({key: "maxChildrenExceeded", params: {count: this.maxChildren}});
        }

        // Check total guests
        if (currentTotal > this.maxTotalGuests) {
            errors.push({key: "maxGuestsExceeded", params: {count: this.maxTotalGuests}});
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    }

    // Computed getters
    get totalGuests(): number {
        return this.adults.value + this.children.value;
    }

    get totalPets(): number {
        return this.pets.value;
    }

    /** Returns true if pets are not allowed at this property */
    get petsNotAllowed(): boolean {
        return !this.allowsPets;
    }

    /** Returns true if the pets toggle is enabled (1 pet) */
    get petsEnabled(): boolean {
        return this.pets.value > 0;
    }

    togglePets(enabled: boolean): void {
        this.pets.setValue(enabled ? 1 : 0);
        this.emitChange();
    }

    /** Returns the maximum number of pets allowed */
    get maxPetsAllowed(): number {
        return this.maxPets;
    }

    get value(): {adults: number; children: number; pets: number} {
        return {
            adults: this.adults.value,
            children: this.children.value,
            pets: this.pets.value,
        };
    }

    // Check if guests have non-default values
    get hasValue(): boolean {
        return this.adults.value > 0 || this.children.value > 0 || this.pets.value > 0;
    }

    // Get all query params as string
    getQueryParam(): string {
        const params = new URLSearchParams();

        const values = {
            adults: this.adults.value,
            children: this.children.value,
            pets: this.pets.value,
        };

        Object.entries(values).forEach(([key, value]) => {
            if (value > 0) params.append(key, String(value));
        });

        return params.toString();
    }

    // Set values from query params (skipClamp=true to show invalid values with error)
    setFromQueryParams(params: URLSearchParams | Record<string, string | undefined>): void {
        const get = (key: string): string | null | undefined => (params instanceof URLSearchParams ? params.get(key) : params[key]);

        const map = {
            adults: {field: this.adults, defaultValue: 1},
            children: {field: this.children, defaultValue: 0},
            pets: {field: this.pets, defaultValue: 0},
        } as const;

        Object.entries(map).forEach(([key, {field, defaultValue}]) => {
            const paramValue = get(key);
            let valueToSet: number;

            if (paramValue === null || paramValue === undefined) {
                // Parameter not in URL - use default value
                valueToSet = defaultValue;
            } else {
                const parsedValue = parseInt(paramValue, 10);
                // skipClamp=true to allow invalid URL values to be displayed (with error shown)
                if (!isNaN(parsedValue) && parsedValue >= 0) {
                    valueToSet = parsedValue;
                } else {
                    // Invalid value - use default
                    valueToSet = defaultValue;
                }
            }

            // Always set value, even if same, to ensure UI updates on history navigation
            // Use silent=true to avoid triggering change events during URL sync
            field.setValue(valueToSet, true, true);
        });

        // Update limits after setting values
        this.updateDynamicLimits();
    }

    // Actions
    private emitChange(): void {
        this.events.emit("change", this.value);
    }

    apply(): void {
        this.events.emit("apply");
    }

    sync(config: GuestsStepperConfig): void {
        if (config.initialAdults !== undefined) {
            this.adults.setValue(config.initialAdults);
        }
        if (config.initialChildren !== undefined) {
            this.children.setValue(config.initialChildren);
        }
        if (config.initialPets !== undefined) {
            this.pets.setValue(config.initialPets);
        }
    }

    reset(): void {
        this.adults.reset(1);
        this.children.reset(0);
        this.pets.reset(0);
        this.emitChange();
        this.apply();
    }

    openDropDown(): void {
        this.events.emit("open");
    }
}

// Export type
export type GuestsStepperInstance = GuestsStepperClass;

// Factory function (SSR compatible)
export function GuestsStepper(config?: GuestsStepperConfig): GuestsStepperInstance {
    return proxy<GuestsStepperInstance>(new GuestsStepperClass(config));
}
